import React, { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
} from "@mui/material";
import ScrollableTableContainer from "../../components/common/ScrollableTableContainer";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import CalendarEventModal from "./CalendarEventModal";

import {
  useGetMyCalendarEventsQuery, // ✅ FIX
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
} from "../../redux/admin/calendarApi";

function fmt(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function getErrMsg(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err?.data?.msg) return err.data.msg;
  if (err?.data?.message) return err.data.message;
  if (err?.error) return err.error;
  if (err?.status) return `Request failed (${err.status})`;
  try {
    return JSON.stringify(err);
  } catch {
    return "Request failed";
  }
}

export default function CalendarManager() {
  // ✅ FIX: useGetMyCalendarEventsQuery
  const { data, isLoading, isError, error, refetch } = useGetMyCalendarEventsQuery();

  // ✅ normalize different backend shapes safely
  const events = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.events)) return data.events;
    if (Array.isArray(data.data)) return data.data;
    return [];
  }, [data]);

  const [createEvent, createState] = useCreateCalendarEventMutation();
  const [updateEvent, updateState] = useUpdateCalendarEventMutation();
  const [deleteEvent, deleteState] = useDeleteCalendarEventMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create|edit
  const [editData, setEditData] = useState(null);

  const [uiMsg, setUiMsg] = useState("");

  const openCreate = () => {
    setUiMsg("");
    setModalMode("create");
    setEditData(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setUiMsg("");
    setModalMode("edit");
    setEditData(row);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (payload) => {
    if (modalMode === "edit") {
      await updateEvent({ id: editData._id, ...payload }).unwrap();
      setUiMsg("Updated ✅");
    } else {
      await createEvent(payload).unwrap();
      setUiMsg("Created ✅");
    }
    refetch?.();
  };

  const handleDelete = async (row) => {
    setUiMsg("");
    const ok = window.confirm(`Delete "${row?.title || "event"}" ?`);
    if (!ok) return;

    try {
      await deleteEvent(row._id).unwrap();
      setUiMsg("Deleted ✅");
      refetch?.();
    } catch (e) {
      setUiMsg(getErrMsg(e));
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 1.2, md: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontWeight: 900, fontSize: 22 }}>Calendar</Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ fontWeight: 900, borderRadius: 2 }}
        >
          New
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {!!uiMsg && (
        <Alert
          severity={uiMsg.includes("fail") || uiMsg.includes("Request") ? "error" : "success"}
          sx={{ mb: 2 }}
        >
          {uiMsg}
        </Alert>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrMsg(error)}
        </Alert>
      )}

      <ScrollableTableContainer sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 900 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Recurrence</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>From</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>To</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 900 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading...</TableCell>
              </TableRow>
            ) : events.length ? (
              events.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 800 }}>{row.title || "-"}</Typography>
                    {!!row.message && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {row.message}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={(row.recurrence || "daily").toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>{fmt(row.from)}</TableCell>
                  <TableCell>{fmt(row.to)}</TableCell>

                  <TableCell>
                    <Chip
                      label={row.isActive === false ? "INACTIVE" : "ACTIVE"}
                      size="small"
                      color={row.isActive === false ? "default" : "success"}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEdit(row)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          onClick={() => handleDelete(row)}
                          size="small"
                          disabled={deleteState.isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>No events</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollableTableContainer>

      <CalendarEventModal
        open={modalOpen}
        onClose={closeModal}
        mode={modalMode}
        initialData={editData}
        onSubmit={handleSubmit}
        isLoading={createState.isLoading || updateState.isLoading}
      />
    </Paper>
  );
}
